import { useNavigate } from 'react-router-dom';
import {
  Search,
  Sun,
  Moon,
  Monitor,
  LogOut,
  User,
  Settings,
  Menu,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';
import { ROUTES } from '@/constants/routes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar } from '@/components/ui/avatar';
import {
  Dropdown,
  DropdownItem,
  DropdownSeparator,
  DropdownLabel,
} from '@/components/ui/dropdown';

export function Header() {
  const navigate = useNavigate();
  const { theme, setTheme, sidebarCollapsed, setMobileMenuOpen } = useUIStore();
  const { user, logout } = useAuthStore();

  const handleLogout = () => {
    logout();
    navigate(ROUTES.LOGIN);
  };

  const themeIcon = {
    light: <Sun className="h-4 w-4" />,
    dark: <Moon className="h-4 w-4" />,
    system: <Monitor className="h-4 w-4" />,
  };

  return (
    <header
      className={cn(
        'fixed top-0 right-0 z-30 flex h-16 items-center justify-between border-b bg-background px-4 transition-all duration-300',
        sidebarCollapsed ? 'left-[68px]' : 'left-64'
      )}
    >
      {/* Left section - Mobile menu + Search */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={() => setMobileMenuOpen(true)}
        >
          <Menu className="h-5 w-5" />
        </Button>

        <div className="hidden md:block relative">
          <Input
            placeholder="Search..."
            className="w-64 pl-10"
            leftIcon={<Search className="h-4 w-4" />}
          />
        </div>
      </div>

      {/* Right section - Actions */}
      <div className="flex items-center gap-2">
        {/* Theme toggle */}
        <Dropdown
          trigger={
            <Button variant="ghost" size="icon">
              {themeIcon[theme]}
            </Button>
          }
        >
          <DropdownLabel>Theme</DropdownLabel>
          <DropdownItem
            icon={<Sun className="h-4 w-4" />}
            onClick={() => setTheme('light')}
          >
            Light
          </DropdownItem>
          <DropdownItem
            icon={<Moon className="h-4 w-4" />}
            onClick={() => setTheme('dark')}
          >
            Dark
          </DropdownItem>
          <DropdownItem
            icon={<Monitor className="h-4 w-4" />}
            onClick={() => setTheme('system')}
          >
            System
          </DropdownItem>
        </Dropdown>

        {/* User menu */}
        <Dropdown
          trigger={
            <button className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-accent transition-colors">
              <Avatar src={user?.avatar} alt={user?.name} size="sm" />
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground capitalize">
                  {user?.role?.replace('_', ' ')}
                </p>
              </div>
            </button>
          }
          className="w-56"
        >
          <div className="px-2 py-1.5">
            <p className="text-sm font-medium">{user?.name}</p>
            <p className="text-xs text-muted-foreground">{user?.email}</p>
          </div>
          <DropdownSeparator />
          <DropdownItem
            icon={<User className="h-4 w-4" />}
            onClick={() => navigate(ROUTES.PROFILE)}
          >
            Profile
          </DropdownItem>
          <DropdownItem
            icon={<Settings className="h-4 w-4" />}
            onClick={() => navigate(ROUTES.SETTINGS)}
          >
            Settings
          </DropdownItem>
          <DropdownSeparator />
          <DropdownItem
            icon={<LogOut className="h-4 w-4" />}
            destructive
            onClick={handleLogout}
          >
            Log out
          </DropdownItem>
        </Dropdown>
      </div>
    </header>
  );
}